import React, { useState } from "react";
import { motion } from "framer-motion";
import { Download, Cpu, Bot } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSiteContent } from "../../context/SiteContent";

const SpecTable = ({ rows, testid }) => (
  <div className="rounded-xl border border-helios-line overflow-hidden" data-testid={testid}>
    {rows.map((r, i) => (
      <div key={i} className={`grid grid-cols-3 gap-3 px-4 py-3 ${i % 2 ? "bg-helios-mist" : "bg-white"}`}>
        <span className="col-span-1 font-mono text-[10.5px] uppercase tracking-wider text-slate-500 pt-0.5">{r.label}</span>
        <span className="col-span-2 text-[13.5px] text-helios-slate leading-snug">{r.value}</span>
      </div>
    ))}
  </div>
);

const ModelBlock = ({ series, idx, onDatasheet }) => {
  const models = (series.models || []).filter((m) => m.isActive);
  const [active, setActive] = useState(0);
  const model = models[active] || models[0];
  if (!model) return null;
  const flip = idx % 2 === 1;
  const Icon = idx === 0 ? Cpu : Bot;

  return (
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-start ${idx > 0 ? "mt-24 md:mt-32" : ""}`}>
      <Reveal delay={flip ? 0.1 : 0} className={`lg:sticky lg:top-28 ${flip ? "lg:order-2" : ""}`}>
        <div className="relative rounded-2xl overflow-hidden spotlight p-6 border border-helios-line">
          <span className="font-mono text-[11px] tracking-widest text-slate-400">
            {String(idx + 1).padStart(2, "0")} — {series.seriesName.toUpperCase()}
          </span>
          <div className="relative mt-4 rounded-xl overflow-hidden aspect-[4/3]">
            <img src={model.imageUrl} alt={series.seriesName} className="w-full h-full object-cover" />
          </div>
          <div className="mt-5 flex items-center gap-2 text-helios-slate">
            <Icon size={18} className={idx === 0 ? "text-helios-cyan" : "text-helios-amber"} />
            <span className="font-display font-bold text-xl">{series.seriesName}</span>
          </div>
        </div>
      </Reveal>

      <Reveal delay={flip ? 0 : 0.1} className={flip ? "lg:order-1" : ""}>
        <h3 className="font-display text-2xl md:text-3xl font-bold text-helios-slate">{series.seriesName}</h3>
        <p className="mt-2 text-slate-600 leading-relaxed">{series.description}</p>

        {models.length > 1 && (
          <div className="mt-6 inline-flex items-center rounded-full border border-helios-line bg-helios-mist p-1" data-testid={`series-${series.id}-toggle`}>
            {models.map((m, mi) => {
              const on = active === mi;
              return (
                <button key={m.id} onClick={() => setActive(mi)}
                  className="relative inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-colors"
                  data-testid={`series-${series.id}-model-${mi}`}>
                  {on && <motion.span layoutId={`pill-${series.id}`} className="absolute inset-0 rounded-full bg-white shadow-sm border border-helios-line" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                  <span className={`relative z-10 ${on ? "text-helios-slate" : "text-slate-500"}`}>{m.name.replace(series.seriesName, "").trim() || m.name}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-6"><SpecTable rows={model.specs || []} testid={`series-${series.id}-specs`} /></div>
        <div className="mt-6">
          <button onClick={() => onDatasheet(series.seriesName)}
            className="btn-sheen inline-flex items-center gap-2 rounded-full bg-helios-slate px-6 py-3 text-sm font-semibold text-white hover:bg-helios-slate/90 transition-colors"
            data-testid={`series-${series.id}-datasheet`}>
            <Download size={16} /> Download {series.seriesName} Datasheet
          </button>
        </div>
      </Reveal>
    </div>
  );
};

export const Products = ({ onDatasheet }) => {
  const { content } = useSiteContent();
  const series = content?.productSeries || [];

  return (
    <section id="products" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal className="max-w-2xl mb-16">
          <p className="overline text-helios-cyan mb-3">Product Lines</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-helios-slate leading-[1.05]">
            Two robots. Every solar terrain.
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Purpose-built cleaning platforms — from array-traversing utility parks to all-terrain rooftop crawlers.
          </p>
        </Reveal>
        {series.map((s, i) => (
          <ModelBlock key={s.id} series={s} idx={i} onDatasheet={onDatasheet} />
        ))}
      </div>
    </section>
  );
};
